import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { AlertService } from 'src/app/services/alert.service';
import { ToastConfig } from 'src/app/services/alert.service';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-toast',
  styleUrl: './toast.component.scss', 
  standalone: true,
  imports: [CommonModule],
  template: `
  <div class="toast-container">
    <div 
      *ngFor="let toast of toasts; trackBy: trackByToast"
      class="toast-item"
      [ngClass]="'toast-' + toast.type"
    >
      <div class="toast-header">
        <div class="toast-icon">
          <svg *ngIf="toast.type === 'success'" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
          </svg>
          <svg *ngIf="toast.type === 'error'" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
          <svg *ngIf="toast.type === 'warning'" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
          </svg>
          <svg *ngIf="toast.type === 'info'" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
        </div>
        <div class="toast-content">
          <h4 class="toast-title">{{ toast.title }}</h4>
          <p class="toast-message">{{ toast.message }}</p>
        </div>
        <button 
          *ngIf="toast.showClose"
          class="toast-close"
          (click)="removeToast(toast.id)"
          type="button"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>
    </div>
  </div>
`,
styles: [`
  .toast-container {
    position: fixed;
    top: 20px;
    right: 20px;
    z-index: 99999;
    display: flex;
    flex-direction: column;
    gap: 10px;
    max-width: 400px;
    pointer-events: none;
  }

  .toast-item {
    background: white;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    border-left: 4px solid;
    overflow: hidden;
    pointer-events: auto;
    transform: translateX(0);
    opacity: 1;
    transition: all 0.3s ease;
    animation: slideInRight 0.3s ease-out;
  }

  .toast-item:hover {
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
    transform: translateY(-2px);
  }

  .toast-success {
    border-left-color: #10b981;
  }

  .toast-error {
    border-left-color: #ef4444;
  }

  .toast-warning {
    border-left-color: #f59e0b;
  }

  .toast-info {
    border-left-color: #3b82f6;
  }

  .toast-header {
    display: flex;
    align-items: flex-start;
    padding: 16px;
    gap: 12px;
  }

  .toast-icon {
    flex-shrink: 0;
    margin-top: 2px;
  }

  .toast-success .toast-icon {
    color: #10b981;
  }

  .toast-error .toast-icon {
    color: #ef4444;
  }

  .toast-warning .toast-icon {
    color: #f59e0b;
  }

  .toast-info .toast-icon {
    color: #3b82f6;
  }

  .toast-content {
    flex: 1;
    min-width: 0;
  }

  .toast-title {
    font-weight: 600;
    font-size: 14px;
    color: #1f2937;
    margin: 0 0 4px 0;
    line-height: 1.2;
  }

  .toast-message {
    font-size: 13px;
    color: #6b7280;
    margin: 0;
    line-height: 1.4;
  }

  .toast-close {
    flex-shrink: 0;
    background: none;
    border: none;
    color: #9ca3af;
    cursor: pointer;
    padding: 4px;
    border-radius: 4px;
    transition: all 0.2s;
    margin-top: 2px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .toast-close:hover {
    background-color: #f3f4f6;
    color: #6b7280;
  }

  @keyframes slideInRight {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }

  @keyframes slideOutRight {
    from {
      transform: translateX(0);
      opacity: 1;
    }
    to {
      transform: translateX(100%);
      opacity: 0;
    }
  }

  .toast-item.removing {
    animation: slideOutRight 0.3s ease-in forwards;
  }

  /* Responsive */
  @media (max-width: 640px) {
    .toast-container {
      top: 10px;
      right: 10px;
      left: 10px;
      max-width: none;
    }
    
    .toast-item {
      max-width: 100%;
    }
  }
`]
})
export class ToastComponent implements OnInit, OnDestroy {
  toasts: ToastConfig[] = [];
  private subscription: Subscription = new Subscription();  

  constructor(public alertService: AlertService) {}

  ngOnInit(): void {
    this.subscription = this.alertService.toasts$.subscribe(toasts => {
      this.toasts = toasts;
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  removeToast(id: string): void {
    this.alertService.removeToast(id);
  }

  trackByToast(index: number, toast: ToastConfig): string {
    return toast.id;
  }
}     
